"use client";

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  EyeOffIcon,
  GripVerticalIcon,
  MinusIcon,
  PlusIcon,
} from "lucide-react";
import * as React from "react";

import { cn } from "../cn";
import { Button } from "./button";

// HOME-DASH (Andrei, 2026-10-27: "modeled so people can reorganize things, like dashboards… show
// and hide certain sections and then reorganize them, resize them… so we don't have so much empty
// space"): a full-width widget grid. Twelve columns from 1024 px (six from 768, one below), packed
// densely so a narrow widget fills a hole. Outside edit mode a widget is just its content, and a
// widget whose content renders nothing takes no cell. In edit mode each widget wears a bar: a
// drag handle (drop it on another widget to take that place), earlier / later, narrower / wider
// and hide. The grid only reports intents (`onMove`, `onResize`, `onHide`); the caller owns the
// layout and saves it.

export const DASHBOARD_SIZES = ["xs", "s", "m", "l", "xl", "full"] as const;
export type DashboardSize = (typeof DASHBOARD_SIZES)[number];

/** Column spans: 12 columns from lg, 6 from md, one below. */
const SPAN: Record<DashboardSize, string> = {
  xs: "md:col-span-2 lg:col-span-2",
  s: "md:col-span-2 lg:col-span-3",
  m: "md:col-span-3 lg:col-span-4",
  l: "md:col-span-3 lg:col-span-6",
  xl: "md:col-span-6 lg:col-span-8",
  full: "md:col-span-6 lg:col-span-12",
};

const SIZE_LABEL: Record<DashboardSize, string> = {
  xs: "XS",
  s: "S",
  m: "M",
  l: "L",
  xl: "XL",
  full: "Full width",
};

export type DashboardItem = {
  id: string;
  /** The widget's name (the edit bar and the controls' accessible names). */
  title: string;
  size: DashboardSize;
  content: React.ReactNode;
};

export type DashboardGridProps = Omit<React.ComponentProps<"ul">, "children"> & {
  items: readonly DashboardItem[];
  editing?: boolean;
  /** Move a widget to `index` in `items`' order. */
  onMove?: (id: string, index: number) => void;
  onResize?: (id: string, size: DashboardSize) => void;
  onHide?: (id: string) => void;
};

const DRAG_TYPE = "application/x-companyos-widget";

export function DashboardGrid({
  items,
  editing = false,
  onMove,
  onResize,
  onHide,
  className,
  ...props
}: DashboardGridProps) {
  const [dragging, setDragging] = React.useState<string | null>(null);
  const [over, setOver] = React.useState<string | null>(null);
  const refs = React.useRef(new Map<string, HTMLLIElement>());
  return (
    <ul
      data-slot="dashboard-grid"
      data-editing={editing || undefined}
      className={cn(
        "grid w-full grid-flow-row-dense grid-cols-1 gap-3 md:grid-cols-6 lg:grid-cols-12",
        className,
      )}
      {...props}
    >
      {items.map((item, index) => {
        const step = DASHBOARD_SIZES.indexOf(item.size);
        return (
          <li
            key={item.id}
            ref={(el) => {
              if (el) refs.current.set(item.id, el);
              else refs.current.delete(item.id);
            }}
            data-slot="dashboard-item"
            data-widget={item.id}
            data-size={item.size}
            data-drop-target={(editing && over === item.id && dragging !== item.id) || undefined}
            className={cn(
              "flex min-w-0 flex-col",
              SPAN[item.size],
              editing
                ? "rounded-lg outline-1 outline-border-strong outline-dashed outline-offset-2 data-drop-target:outline-2 data-drop-target:outline-ring"
                : // Content that renders nothing takes no cell.
                  "has-[>[data-slot=dashboard-item-body]:empty]:hidden",
              dragging === item.id && "opacity-50",
            )}
            onDragOver={
              editing
                ? (e) => {
                    if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (over !== item.id) setOver(item.id);
                  }
                : undefined
            }
            onDragLeave={editing ? () => setOver((o) => (o === item.id ? null : o)) : undefined}
            onDrop={
              editing
                ? (e) => {
                    const id = e.dataTransfer.getData(DRAG_TYPE);
                    setOver(null);
                    setDragging(null);
                    if (!id || id === item.id) return;
                    e.preventDefault();
                    onMove?.(id, index);
                  }
                : undefined
            }
          >
            {editing ? (
              <div
                data-slot="dashboard-item-bar"
                // Wraps onto a second line when the widget is narrower than its controls.
                className="mb-1.5 flex min-h-7 min-w-0 flex-wrap items-center gap-0.5 text-xs text-muted-foreground"
              >
                <button
                  type="button"
                  draggable
                  aria-label={`Drag ${item.title}`}
                  title="Drag to move"
                  className="inline-flex size-6 shrink-0 cursor-grab items-center justify-center rounded-sm hover:bg-muted hover:text-foreground active:cursor-grabbing"
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData(DRAG_TYPE, item.id);
                    const el = refs.current.get(item.id);
                    if (el && typeof e.dataTransfer.setDragImage === "function")
                      e.dataTransfer.setDragImage(el, 16, 16);
                    setDragging(item.id);
                  }}
                  onDragEnd={() => {
                    setDragging(null);
                    setOver(null);
                  }}
                >
                  <GripVerticalIcon aria-hidden="true" className="size-3.5" />
                </button>
                <span className="min-w-12 flex-1 basis-12 truncate font-medium text-secondary">
                  {item.title}
                </span>
                {/* The narrowest widgets keep their room for the name. */}
                {item.size === "xs" ? null : (
                  <span className="mr-1 shrink-0 tabular-nums" title="Width">
                    {SIZE_LABEL[item.size]}
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Move ${item.title} earlier`}
                  title="Move earlier"
                  disabled={index === 0}
                  onClick={() => onMove?.(item.id, index - 1)}
                >
                  <ArrowLeftIcon aria-hidden="true" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Move ${item.title} later`}
                  title="Move later"
                  disabled={index === items.length - 1}
                  onClick={() => onMove?.(item.id, index + 1)}
                >
                  <ArrowRightIcon aria-hidden="true" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Make ${item.title} narrower`}
                  title="Narrower"
                  disabled={step <= 0}
                  onClick={() => {
                    const next = DASHBOARD_SIZES[step - 1];
                    if (next) onResize?.(item.id, next);
                  }}
                >
                  <MinusIcon aria-hidden="true" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Make ${item.title} wider`}
                  title="Wider"
                  disabled={step >= DASHBOARD_SIZES.length - 1}
                  onClick={() => {
                    const next = DASHBOARD_SIZES[step + 1];
                    if (next) onResize?.(item.id, next);
                  }}
                >
                  <PlusIcon aria-hidden="true" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Hide ${item.title}`}
                  title="Hide"
                  onClick={() => onHide?.(item.id)}
                >
                  <EyeOffIcon aria-hidden="true" />
                </Button>
              </div>
            ) : null}
            <div
              data-slot="dashboard-item-body"
              className={cn(
                "flex min-w-0 flex-1 flex-col *:flex-1",
                editing &&
                  "empty:min-h-11 empty:items-center empty:justify-center empty:rounded-lg empty:border empty:border-dashed empty:border-border empty:text-xs empty:text-muted-foreground empty:before:content-['Nothing_to_show_now']",
              )}
            >
              {item.content}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
